import api from "@modules/JourneyBuilder/api";
import loadResourceString from "../../../Util/resources";

import {
    sanitizeText,
    withPropertyBasedConnections,
    withNamedPermanentConnection
} from "./brick-initializer-helpers";
import {COLORS} from "@modules/JourneyBuilder/constants";

//When this text gets shown we can assume the rest of the page won't be fully functional either
const PLACEHOLDER_AUDIENCE_NAME = loadResourceString('Generic_Error_Text');

const extractHtml = node => {
    //To get the outerHTML of a node, the node needs a wrapper
    const wrapper = document.createElement('div');
    wrapper.appendChild(node);
    return wrapper.innerHTML;
};

const getFieldTypeLabelByTypeKey = conditionTypeKey => {
    if(!getFieldTypeLabelByTypeKey._fieldKeyToLabelMapPromise){
        getFieldTypeLabelByTypeKey._fieldKeyToLabelMapPromise = api.getAudienceFilterFieldTypes().then(
            fieldTypes => {
                return fieldTypes.reduce((map, {type, label}) => {
                    map.set(type, label);
                    return map;
                }, new Map());
            },
            () => new Map()
        )
    }
    
    //Key itself should in most cases be fine for display
    return getFieldTypeLabelByTypeKey._fieldKeyToLabelMapPromise
        .then(keyToLabelMap => keyToLabelMap.get(conditionTypeKey) || conditionTypeKey);
}

const getFieldValueLabel = async (type, value) => {
    //Ensure our map keys are not case sensitive/won't accidentally match the key of another type 
    const formatKey = (type, value) => `${type}_${value}`.toLowerCase();
    
    if(!getFieldValueLabel._dropdownFieldValueLabelMap){
        //Ensure our mapping is only created once by caching it in our function object
        getFieldValueLabel._dropdownFieldValueLabelMap = api.getAudienceFilterFieldTypes().then(fieldTypes => {
            return fieldTypes.reduce((map, {
                id,
                type,
                value,
                isDropDown
            }) => {
                //Only dropdown values need to have their label (= value) be present
                if(isDropDown){
                    map.set(formatKey(type, id), value);
                }

                return map;
            }, new Map());
        });
    }
    
    const dropdownValueLabelMap = await getFieldValueLabel._dropdownFieldValueLabelMap;
    
    //When our value is a dropdown value; it is expected to be in our map
    //if our value is not a dropdown value, it has been manually entered by the user and should be displayed as such
    return dropdownValueLabelMap.get(formatKey(type, value)) || value;
}

const resolveFieldValue = value => {
    //Ensure we don't create the same map over and over again
    if(!resolveFieldValue._fieldTypeMapPromise){
        resolveFieldValue._fieldTypeMapPromise = api.getAudienceFilterFieldTypes().then(
            fieldTypes => {
                return fieldTypes.reduce((map, fieldType) => {
                    map.set(fieldType.id, {value: fieldType.value, dropdown: fieldType.isDropDown});
                    return map;
                }, new Map());
            },
            //Errors might occur; our map is currently just used for displaying names
            //in case of an error; we can just use a placeholder/generic error as audience name
            () => new Map()
        );
    }

    return resolveFieldValue._fieldTypeMapPromise
        .then(idToNameMap => {
            const field = idToNameMap.get(value);
            return field && field.dropdown ? field.value : value;
        });
}

const buildTooltipFromFields = fields => {
    fields = fields || [];
    
    if(fields.length === 0){
        return Promise.resolve('');
    }

    //Certain fields may require an async call
    //as to not alter the given order, every field element should
    //be exposed as a promise
    const fieldElementPromises = fields.map(field => {
        const fieldValue = field.value || '';
        const fieldType = field.type || '';
        
        //If no fieldType has been selected we can resolve instantly
        let getFieldTypeLabelPromise = fieldType ? 
            getFieldTypeLabelByTypeKey(fieldType) :
            Promise.resolve(fieldType);
        
        //Regular fields can have their configured value displayed
        let getFieldValueLabelPromise = Promise.resolve(fieldValue);
        
        //Audience fields require the given audience name to be displayed
        if(fieldValue) {
            getFieldValueLabelPromise = getFieldValueLabel(fieldType, fieldValue);
        }
        
        return Promise.all([
            getFieldTypeLabelPromise,
            getFieldValueLabelPromise
        ]).then(values => {
            const fieldTypeLabel = values[0];
            const fieldValueLabel = values[1];

            const li = document.createElement('li');
            li.innerText = `${fieldTypeLabel} - ${fieldValueLabel}`;
            return li;
        });
    });
    
    return Promise.all(fieldElementPromises).then(liElements => {
        const ul = document.createElement('ul');
        
        //Every promise should've resolved to a li element
        liElements.forEach(li => ul.appendChild(li));
        
        return extractHtml(ul);
    });
};

/**
 * Initialize the given audienceFilterBrick
 * @param {JourneyBrick} brick
 */
const initialize = brick => {
    withNamedPermanentConnection(brick, {
        connectionIdProperty: 'outflowConnectionId',
        configureConnection: connection => {
            connection.ui.cssClass = 'jc-outflow';
            connection.ui.backgroundColor = COLORS.BRICK_ENDPOINT_OUTFLOW;
            connection.ui.tooltip.content = loadResourceString('JourneyComposer_Shared_OutflowConnection_TooltipContent');
        }
    });
    
    withPropertyBasedConnections(brick, {
        brickConfigProperty: 'conditions',
        canDeleteConnection: connection => connection.id !== brick.config.outflowConnectionId,
        onConnectionUpdated: (connection, condition) => {
            const { ui } = connection;
            const fields = condition.fields || [];
            const tooltipTitle = sanitizeText(condition.name);

            ui.borderColor = condition.color;

            ui.tooltip = {
                title: tooltipTitle,
                content: buildTooltipFromFields(fields)
            };
        } 
    });
}

export {
    initialize
}